import { useEffect, useState } from "react";
import type { CityDetailResponseDto } from "../../shared/api";
import { formatTry } from "../../shared/pricing";
import {
  graphemeCount,
  isValidMessageUrlInput,
  MESSAGE_URL_MAX_LENGTH
} from "../../shared/validation";
import { analyticsSessionId, trackClientEvent } from "../analytics";
import { CityHistory } from "./CityHistory";
import { MessageExternalLink } from "./MessageExternalLink";
import { TurnstileWidget } from "./TurnstileWidget";
import { ReportForm } from "./ReportForm";

interface CityPanelProps {
  city: CityDetailResponseDto | null;
  loading: boolean;
  stale: boolean;
  error: string | null;
  onRefresh: () => boolean | Promise<boolean> | void;
}

function formatCountdown(milliseconds: number) {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds].map((value) => String(value).padStart(2, "0")).join(":");
}

function Countdown({ initialMilliseconds }: { initialMilliseconds: number }) {
  const [remaining, setRemaining] = useState(initialMilliseconds);
  useEffect(() => {
    const interval = window.setInterval(
      () => setRemaining((current) => Math.max(0, current - 1_000)),
      1_000
    );
    return () => window.clearInterval(interval);
  }, []);
  return <>{formatCountdown(remaining)}</>;
}

export function CityPanel({ city, loading, stale, error, onRefresh }: CityPanelProps) {
  const [message, setMessage] = useState("");
  const [nickname, setNickname] = useState("");
  const [url, setUrl] = useState("");
  const [email, setEmail] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const [toolNotice, setToolNotice] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formTracked, setFormTracked] = useState(false);

  useEffect(() => {
    if (!toolNotice || refreshing) return;
    const timeout = window.setTimeout(() => setToolNotice(null), 3_500);
    return () => window.clearTimeout(timeout);
  }, [refreshing, toolNotice]);

  if (loading && !city) {
    return (
      <aside className="city-panel city-panel--loading" aria-live="polite">
        Şehir yükleniyor…
      </aside>
    );
  }

  if (!city && error) {
    return (
      <aside className="city-panel city-panel--empty" role="alert">
        <span className="panel-index">Şehir doğrulanamadı</span>
        <h2>Bilgiye ulaşılamıyor.</h2>
        <p>{error} Haritadaki diğer şehirleri inceleyebilir veya yeniden deneyebilirsin.</p>
        <button className="primary-button" type="button" onClick={onRefresh}>
          Yeniden dene
        </button>
      </aside>
    );
  }

  if (!city) {
    return (
      <aside className="city-panel city-panel--empty">
        <span className="panel-index">81 şehir / tek günlük alan</span>
        <h2>Bir şehre dokun.</h2>
        <p>Bugünün mesajını, sıradaki fiyatı ve gece yarısına kalan süreyi burada göreceksin.</p>
      </aside>
    );
  }

  const publishingDisabled =
    submitting ||
    city.availability.readOnly ||
    !city.salesOpen ||
    !email.includes("@") ||
    !turnstileToken ||
    !isValidMessageUrlInput(url) ||
    graphemeCount(message) < 1 ||
    graphemeCount(message) > 140;

  return (
    <aside className="city-panel" aria-live="polite">
      <div className="city-panel__heading">
        <div>
          <span className="panel-index">{city.code}</span>
          <h2>{city.name}</h2>
        </div>
        <div className="city-panel__tools">
          <button
            className="icon-button"
            type="button"
            title="Şehir bağlantısını kopyala"
            data-tooltip="Şehir bağlantısını kopyala"
            onClick={() => {
              const shareUrl = `${window.location.origin}/?city=${city.code}&ref=share`;
              if (!navigator.clipboard?.writeText) {
                setToolNotice("Bağlantı kopyalanamadı. Tarayıcı panoya erişemiyor.");
                return;
              }
              void navigator.clipboard
                .writeText(shareUrl)
                .then(() => {
                  setToolNotice("Şehir bağlantısı panoya kopyalandı.");
                  trackClientEvent("share_referral", {
                    cityCode: city.code,
                    source: "internal"
                  });
                })
                .catch(() =>
                  setToolNotice("Bağlantı kopyalanamadı. Tarayıcı izinlerini kontrol et.")
                );
            }}
            aria-label={`${city.name} bağlantısını paylaş`}
          >
            ↗
          </button>
          <button
            className="icon-button"
            type="button"
            title="Şehir bilgilerini yenile"
            data-tooltip="Şehir bilgilerini yenile"
            disabled={refreshing}
            onClick={() => {
              setRefreshing(true);
              setToolNotice("Şehir bilgileri yenileniyor…");
              void Promise.resolve(onRefresh())
                .then((refreshed) => {
                  setToolNotice(
                    refreshed === false
                      ? "Şehir bilgileri yenilenemedi."
                      : "Şehir bilgileri yenilendi."
                  );
                })
                .catch(() => setToolNotice("Şehir bilgileri yenilenemedi."))
                .finally(() => setRefreshing(false));
            }}
            aria-label={`${city.name} verisini yenile`}
          >
            ↻
          </button>
        </div>
      </div>

      {toolNotice && (
        <div className="tool-toast" role="status" aria-live="polite">
          {toolNotice}
        </div>
      )}

      {(stale || error) && (
        <p className="status-note status-note--warning">
          Son doğrulanmış bilgi gösteriliyor. {error}
        </p>
      )}
      {city.availability.readOnly && (
        <p className="status-note">Harita salt okunur. Yeni ödeme alınmıyor.</p>
      )}

      <div className={`message-card message-card--${city.status}`}>
        <span className="message-card__label">
          {city.status === "paid" ? "Bugünün sesi" : "Bugün mesaj yok"}
        </span>
        <blockquote>
          {city.message ?? "Bugün henüz bir mesaj yok. Şehrin ilk sesi sen ol."}
        </blockquote>
        {(city.nickname || city.publishedAt) && (
          <div className="message-card__meta">
            {city.nickname && <cite>— {city.nickname}</cite>}
            {city.publishedAt && (
              <time dateTime={city.publishedAt}>
                {new Intl.DateTimeFormat("tr-TR", {
                  timeZone: "Europe/Istanbul",
                  hour: "2-digit",
                  minute: "2-digit"
                }).format(new Date(city.publishedAt))}
              </time>
            )}
          </div>
        )}
        {city.url && <MessageExternalLink url={city.url} className="message-card__external-link" />}
      </div>
      {city.status === "paid" && city.messageId && <ReportForm messageId={city.messageId} />}

      <dl className="city-stats">
        <div>
          <dt>Sıradaki fiyat</dt>
          <dd>{formatTry(BigInt(city.nextPriceMinor))}</dd>
        </div>
        <div>
          <dt>Gece yarısına</dt>
          <dd className="countdown">
            <Countdown initialMilliseconds={city.millisecondsUntilMidnight} />
          </dd>
        </div>
      </dl>

      <form
        className="publish-form"
        onFocus={() => {
          if (formTracked) return;
          setFormTracked(true);
          trackClientEvent("form_started", { cityCode: city.code, source: "internal" });
        }}
        onSubmit={(event) => {
          event.preventDefault();
          if (publishingDisabled) return;
          setSubmitting(true);
          setNotice(null);
          void fetch("/api/checkout", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              cityCode: city.code,
              expectedVersion: city.version,
              amountMinor: city.nextPriceMinor,
              email,
              message,
              nickname,
              url,
              turnstileToken,
              analyticsSessionId: analyticsSessionId()
            })
          })
            .then(async (response) => {
              const payload = (await response.json()) as {
                checkoutUrl?: string;
                error?: { message?: string };
              };
              if (!response.ok || !payload.checkoutUrl) {
                throw new Error(payload.error?.message ?? "Ödeme başlatılamadı.");
              }
              window.location.assign(payload.checkoutUrl);
            })
            .catch((checkoutError: unknown) => {
              setSubmitting(false);
              setTurnstileToken("");
              setNotice(
                checkoutError instanceof Error ? checkoutError.message : "Ödeme başlatılamadı."
              );
            });
        }}
      >
        <label htmlFor="message">Şehrin mesajı</label>
        <textarea
          id="message"
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          rows={4}
          maxLength={560}
          placeholder="Bugün bu şehir ne söylesin?"
          disabled={city.availability.readOnly || !city.salesOpen}
        />
        <span
          className={`character-count${graphemeCount(message) > 140 ? " character-count--over" : ""}`}
        >
          {graphemeCount(message)} / 140
        </span>
        <label htmlFor="nickname">
          Takma ad <small>isteğe bağlı</small>
        </label>
        <input
          id="nickname"
          value={nickname}
          onChange={(event) => setNickname(event.target.value)}
          maxLength={120}
          placeholder="Örn. Gece Vapuru"
          disabled={city.availability.readOnly || !city.salesOpen}
        />
        <label htmlFor="url">
          Bağlantı <small>isteğe bağlı</small>
        </label>
        <input
          id="url"
          value={url}
          onChange={(event) => setUrl(event.target.value)}
          type="url"
          inputMode="url"
          maxLength={MESSAGE_URL_MAX_LENGTH}
          placeholder="https://example.com/duyuru"
          aria-invalid={!isValidMessageUrlInput(url)}
          aria-describedby={!isValidMessageUrlInput(url) ? "url-error" : undefined}
          disabled={city.availability.readOnly || !city.salesOpen}
        />
        {!isValidMessageUrlInput(url) && (
          <p className="field-error" id="url-error">
            http:// veya https:// ile başlayan geçerli bir adres gir.
          </p>
        )}
        <label htmlFor="email">Ödeme e-postası</label>
        <input
          id="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          type="email"
          autoComplete="email"
          placeholder="sen@example.com"
          disabled={city.availability.readOnly || !city.salesOpen}
        />
        {!city.availability.readOnly && city.salesOpen && (
          <TurnstileWidget onVerify={setTurnstileToken} />
        )}
        <button className="primary-button" type="submit" disabled={publishingDisabled}>
          {submitting
            ? "Güvenli ödeme hazırlanıyor…"
            : city.salesOpen
              ? `${formatTry(BigInt(city.nextPriceMinor))} ile devam et`
              : "Bugünün satışı kapandı"}
        </button>
        {notice && <p className="form-notice">{notice}</p>}
      </form>

      <CityHistory
        key={`${city.code}:${city.version}`}
        cityCode={city.code}
        cityName={city.name}
        currentLocalDate={city.localDate}
      />
    </aside>
  );
}
